import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type {
  ClusterBanditType,
  Collection,
  TaskStatus,
} from "@bandit-network/sdk-api";
import { QuestSDK } from "@bandit-network/quest-sdk";
import { useConnect } from "@bandit-network/react-wallet-kit";
import { useWallet } from "@solana/wallet-adapter-react";
import { useAccount } from "wagmi";
import { ModuleTask, QuestModule } from "../types";
import { BlockchainEngineType } from "@bandit-network/react-wallet-kit";

type QuestContextType = {
  questId: number;
  setCurrentTaskId: (id: string) => void;
  currentTaskId: string;
  modulesCompleted: Array<QuestModule>;
  setCurrentModuleId: (id: string) => void;
  currentModuleId: string;
  tasksCompleted: Array<string>;
  quest: Collection;
  isLoading: boolean;
  questSDK: QuestSDK;
  tasksList: Array<ModuleTask>;
  address: string;
  isConnected: boolean;
  fetchTasksStatus: () => Promise<Array<TaskStatus>> | null;
  isClaimed: boolean;
  nonce: string;
  nonceExpireBy: Date;
  tokensEligibleToClaim: number;
  isQuestReady: boolean;
  isFetchingNonce: boolean;
  failedToLoadCollection: boolean;
};

export const QuestContext = createContext<QuestContextType>({
  questId: null,
  currentTaskId: "",
  setCurrentTaskId: (id: string) => {},
  modulesCompleted: [],
  setCurrentModuleId: (id: string) => {},
  currentModuleId: "",
  tasksCompleted: [],
  quest: null,
  isLoading: true,
  questSDK: null,
  tasksList: [],
  address: "",
  isConnected: false,
  fetchTasksStatus: async () => null,
  isClaimed: false,
  nonce: "",
  nonceExpireBy: null,
  tokensEligibleToClaim: 0,
  isQuestReady: false,
  isFetchingNonce: false,
  failedToLoadCollection: false,
});

interface QuestContextProps {
  questId: number;
  children: React.ReactNode;
  apiKey: string;
  cluster: ClusterBanditType;
}

export const QuestContextProvider: React.FC<QuestContextProps> = ({
  questId,
  children,
  apiKey,
  cluster,
}) => {
  const questSDK = new QuestSDK(apiKey, cluster);
  const [isLoading, setIsLoading] = useState(true);
  const [failedToLoadCollection, setFailedToLoadCollection] = useState(false);
  const [isQuestReady, setIsQuestReady] = useState(false);
  const [isFetchingNonce, setIsFetchingNonce] = useState(false);
  const [quest, setQuest] = useState<Collection>();
  const [currentTaskId, setCurrentTaskId] = useState<string>("");
  const [nonce, setNonce] = useState<string>("");
  const [nonceExpireBy, setNonceExpireBy] = useState<Date>();
  const [currentModuleId, setCurrentModuleId] = useState<string>();
  const [tasksCompleted, setTasksCompleted] = useState<Array<string>>([]);
  const [modulesClaimed, setModulesClaimed] = useState<Array<string>>([]);
  const [isClaimed, setIsClaimed] = useState(false);
  const {
    isConnectedEVM,
    isConnectedSOL,
    blockchainEngine,
    setBlockchainEngine,
  } = useConnect();
  const { publicKey: solanaAddress } = useWallet();
  const { address: evmAddress } = useAccount();

  const address =
    blockchainEngine === BlockchainEngineType.SOL
      ? solanaAddress?.toBase58()
      : evmAddress;

  const isConnected =
    blockchainEngine === BlockchainEngineType.SOL
      ? isConnectedSOL
      : isConnectedEVM;

  const fetchQuest = useCallback(async () => {
    try {
      setIsLoading(true);
      const _quest = await questSDK.getCollectionById(questId);
      setQuest(_quest);
      setCurrentModuleId(_quest?.contestMetaData?.[0].id);
      const blockchainEngine = [9090, 9091].includes(_quest?.contract.chainId)
        ? BlockchainEngineType.SOL
        : BlockchainEngineType.EVM;
      setBlockchainEngine(blockchainEngine);
    } catch (error) {
      setFailedToLoadCollection(true);
    } finally {
      setIsLoading(false);
    }
  }, [questId]);

  const fetchNonce = useCallback(async () => {
    try {
      setIsFetchingNonce(false);
      if (!isConnected) return;
      const { nonce, expBy } = await questSDK.getNonce(address);
      setNonce(nonce);
      setNonceExpireBy(new Date(expBy));
      setIsFetchingNonce(true);
    } catch (error) {}
  }, [isConnected, address]);

  const setCurrentTask = useCallback(
    (id: string) => {
      setCurrentTaskId(id);
    },
    [setCurrentTaskId],
  );

  const setCurrentModule = useCallback(
    (id: string) => {
      setCurrentModuleId(id);
    },
    [setCurrentModuleId],
  );

  const fetchTasksStatus = useCallback(async () => {
    try {
      setIsQuestReady(false);
      if (!isConnected || !address) return;
      const status = await questSDK.getTasksStatus(questId, address);
      // TODO:
      const completedTasks: Array<string> = status.reduce(
        (flat: Array<string>, questModule) => {
          return (flat = [...flat, ...questModule.tasksCompleted]);
        },
        [],
      );
      const isAllClaimed = status.every((s) => s.isClaimed);
      const _modulesClaimed = [];
      status.forEach((m) => {
        if (m.isClaimed) {
          _modulesClaimed.push(m.moduleId);
        }
      });
      setIsClaimed(isAllClaimed);
      setTasksCompleted(completedTasks);
      setModulesClaimed(_modulesClaimed);
      setIsQuestReady(true);

      return status;
    } catch (error) {}
  }, [isConnected, address]);

  const tasksList = useMemo(() => {
    // TODO:
    if (!quest) return [];
    const list = quest.contestMetaData?.reduce(
      (flat: any, questModule: any) => {
        return (flat = [
          ...flat,
          ...questModule.tasks.map((task) => ({
            task,
            moduleId: questModule.id,
            isCompleted: tasksCompleted.includes(task.id),
          })),
        ]);
      },
      [],
    );
    return list;
  }, [quest, tasksCompleted]);

  const modulesCompleted: Array<QuestModule> = useMemo(() => {
    const mod = {};

    for (let i = 0; i < tasksList?.length; i++) {
      const moduleId = tasksList?.[i].moduleId;
      if (!mod[moduleId]) {
        mod[moduleId] = [];
      }
      // Check if task is completed
      if (tasksList?.[i].isCompleted) {
        mod[moduleId].push(tasksList?.[i].task.id);
      }
    }

    return !!quest?.contestMetaData
      ? quest?.contestMetaData?.filter(
          (m) => m.tasks.length === mod[m.id].length,
        )
      : [];
  }, [quest, tasksList]);

  useEffect(() => {
    fetchQuest();
  }, [fetchQuest]);
  useEffect(() => {
    if (
      !!blockchainEngine &&
      ((blockchainEngine === BlockchainEngineType.SOL && isConnectedSOL) ||
        (blockchainEngine === BlockchainEngineType.EVM && isConnectedEVM))
    )
      fetchNonce();
  }, [fetchNonce, blockchainEngine, isConnectedEVM, isConnectedSOL]);

  useEffect(() => {
    fetchTasksStatus();
  }, [fetchTasksStatus]);

  const tokensEligibleToClaim = useMemo(() => {
    let eligibleTokensClaimsCount = 0;
    if (!!modulesCompleted.length) {
      eligibleTokensClaimsCount = modulesCompleted.reduce(
        (count, questModule) => {
          if (!modulesClaimed.includes(questModule.id)) {
            return count + questModule.rewardData?.count;
          }
          return count;
        },
        0,
      );
    }
    return eligibleTokensClaimsCount;
  }, [modulesCompleted, modulesClaimed]);

  const values = useMemo(
    () => ({
      questId,
      currentTaskId,
      setCurrentTaskId: setCurrentTask,
      modulesCompleted,
      setCurrentModuleId: setCurrentModule,
      currentModuleId,
      tasksCompleted,
      quest,
      isLoading,
      questSDK,
      tasksList,
      address,
      isConnected,
      fetchTasksStatus,
      isClaimed,
      nonce,
      nonceExpireBy,
      tokensEligibleToClaim,
      isQuestReady,
      isFetchingNonce,
      failedToLoadCollection,
    }),
    [
      questId,
      currentTaskId,
      setCurrentTask,
      modulesCompleted,
      setCurrentModule,
      currentModuleId,
      tasksCompleted,
      quest,
      isLoading,
      questSDK,
      tasksList,
      address,
      isConnected,
      fetchTasksStatus,
      isClaimed,
      nonce,
      nonceExpireBy,
      tokensEligibleToClaim,
      isQuestReady,
      isFetchingNonce,
      failedToLoadCollection,
    ],
  );

  return (
    <QuestContext.Provider value={values}>{children}</QuestContext.Provider>
  );
};

export const useQuestContext = (): QuestContextType => useContext(QuestContext);
